import React from 'react';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

const GLYPHS = {
  overview: 'M4 11.2 12 4l8 7.2V20H4V11.2zm5 8.8v-5h6v5',
  dashboard: 'M4 11.2 12 4l8 7.2V20H4V11.2zm5 8.8v-5h6v5',
  tasks: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M8.2 12.2l2.6 2.6 5-5.2',
  assistant: 'M12 3.2l1.5 4.3L17.8 9l-4.3 1.5L12 14.8l-1.5-4.3L6.2 9l4.3-1.5L12 3.2z',
  projects: 'M4 6.5h16v4.2H4V6.5zm0 6.8h16V17.5H4v-4.2z',
  settings: 'M4 7h16M4 12h16M4 17h16',
};

function lightTap() {
  try {
    if (!Capacitor || Capacitor.getPlatform() !== 'ios') return;
    const pending = Haptics.impact({ style: ImpactStyle.Light });
    if (pending && typeof pending.catch === 'function') pending.catch(function () {});
  } catch (error) {
    // Web and desktop have no haptic engine.
  }
}

function TabGlyph({ id }) {
  const d = GLYPHS[id] || GLYPHS.settings;
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Phone tab bar for the agent and homeowner apps. Labels stay on one line.
 * The navy pill sits behind the selected icon. A light haptic fires on iOS only.
 */
export default function AppTabBar({ tabs, activeId, onSelect, colors, isDark, badgeFor, fixed }) {
  const surface = (colors && colors.surface) || '#ffffff';
  const border = (colors && colors.border) || '#e2e8f0';
  const muted = (colors && colors.textSecondary) || '#64748b';
  return (
    <div
      className={`ios-tab-bar app-tab-bar${fixed === false ? ' is-inline' : ''}`}
      style={{
        background: surface,
        borderTop: `1px solid ${border}`,
        '--tab-muted': muted,
        '--tab-ink': isDark ? '#f8fafc' : '#0f172a',
        '--tab-pill-ring': isDark ? 'rgba(248, 250, 252, 0.28)' : 'transparent',
        '--tab-press': isDark ? 'rgba(248, 250, 252, 0.14)' : 'rgba(15, 23, 42, 0.08)',
        '--tab-badge-ring': surface,
      }}
    >
      <style>{`
        .app-tab-bar {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          display: flex;
          align-items: stretch;
          justify-content: space-around;
          box-sizing: border-box;
          padding-top: 4px;
          padding-bottom: env(safe-area-inset-bottom, 0px);
          padding-left: env(safe-area-inset-left, 0px);
          padding-right: env(safe-area-inset-right, 0px);
          box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.08);
          overflow: visible;
        }
        .app-tab-bar.is-inline {
          position: relative;
          padding-bottom: 0;
          box-shadow: none;
        }
        .app-tab-bar .ios-tab-item {
          position: relative;
          flex: 1 1 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2px;
          width: auto;
          min-width: 0 !important;
          max-width: none;
          margin: 2px 1px 4px;
          padding: 4px 1px 5px !important;
          min-height: 58px;
          border: none;
          border-radius: 14px !important;
          background: transparent;
          box-shadow: none;
          transform: none;
          color: var(--tab-muted);
          font-family: inherit;
          cursor: pointer;
          -webkit-appearance: none;
          appearance: none;
          -webkit-tap-highlight-color: transparent;
          overflow: visible;
          transition: background 0.12s ease, opacity 0.12s ease;
        }
        .app-tab-bar .ios-tab-item.active {
          color: var(--tab-ink);
          background: transparent;
          transform: none;
          box-shadow: none;
        }
        .app-tab-bar .ios-tab-item:active {
          transform: none !important;
          background: var(--tab-press) !important;
          opacity: 0.92;
        }
        .app-tab-bar .ios-tab-icon {
          width: 32px;
          height: 24px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: inherit;
        }
        .app-tab-bar .ios-tab-item.active .ios-tab-icon {
          background: #0f172a;
          color: #ffffff;
          box-shadow: inset 0 0 0 1px var(--tab-pill-ring);
        }
        .app-tab-bar .ios-tab-icon svg {
          width: 18px;
          height: 18px;
          display: block;
        }
        .app-tab-bar .ios-tab-label {
          display: block;
          max-width: 100%;
          font-size: 13px !important;
          font-weight: 600;
          line-height: 1.1;
          letter-spacing: -0.02em;
          white-space: nowrap !important;
          overflow: visible;
          text-overflow: clip;
        }
        .app-tab-bar .ios-tab-item:not(.active) .ios-tab-label {
          font-weight: 500;
        }
        .app-tab-bar .ios-tab-badge {
          position: absolute;
          top: 1px;
          left: 50%;
          margin-left: 8px;
          background: #ff3b30;
          color: #fff;
          border-radius: 10px;
          min-width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 650;
          padding: 0 4px;
          border: 2px solid var(--tab-badge-ring);
          z-index: 2;
          pointer-events: none;
        }
      `}</style>
      {(tabs || []).map((tab) => {
        const selected = tab.id === activeId;
        const badge = badgeFor ? badgeFor(tab) : 0;
        return (
          <button
            key={tab.id}
            type="button"
            aria-current={selected ? 'page' : undefined}
            aria-label={tab.label}
            data-tour={tab.id}
            className={`ios-tab-item${selected ? ' active' : ''}`}
            onClick={() => {
              lightTap();
              if (onSelect) onSelect(tab.id);
            }}
          >
            <span className="ios-tab-icon">
              <TabGlyph id={tab.id} />
            </span>
            <span className="ios-tab-label">{tab.label}</span>
            {badge > 0 ? <span className="ios-tab-badge">{badge}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
